// Audit & Playback — D8 surface.
//
// First-cut event stream. Each row is a synthetic line that matches the kinds
// of bus events the rail emits (mint, redeem, settle, kyc, fraud, invariant
// check). When the live trace pane lands we swap the static feed for a
// subscription to invoke('subscribe_audit') with the same row shape.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SeedBanner, SeedPill } from "../components/SeedBanner";

interface Row {
  ts: string;
  tag: "mint" | "redeem" | "settle" | "kyc" | "fraud" | "invariant";
  body: string;
}

const SEED: Row[] = [
  { ts: "14:42:07", tag: "invariant", body: "block #18429 sealed · minted=backed · replica lag 2ms" },
  { ts: "14:42:05", tag: "settle", body: "D5 → sponsor · NIP · ₦42,300 · ref 0x8c1a" },
  { ts: "14:42:04", tag: "redeem", body: "tag 4f2a → POS#1183 · A2Pay store cash-out · R220.00" },
  { ts: "14:42:03", tag: "kyc", body: "D4 step-up requested for tag 91e8 · tier T1 → T2" },
  { ts: "14:42:02", tag: "mint", body: "BVNDLE → D1 · 540 coins · backed · ref 0x8c19" },
  { ts: "14:42:01", tag: "fraud", body: "D7 score 0.93 · hold tag a7c2 · 2-track adj queued" },
  { ts: "14:42:00", tag: "settle", body: "D5 → sponsor · PayShap · R2,450.00 · ref 0x8c18" },
  { ts: "14:41:59", tag: "redeem", body: "tag 1b09 → bills_payment · MTN airtime · 200 NGN" },
  { ts: "14:41:58", tag: "invariant", body: "block #18428 sealed · minted=backed · replica lag 2ms" },
  { ts: "14:41:57", tag: "mint", body: "BVNDLE → D1 · 1,200 coins · backed · ref 0x8c17" },
  { ts: "14:41:55", tag: "redeem", body: "tag c0d8 → user_partner_transfer · MERCH#42 · ₦18,000" },
];

const TAG_CLASS: Record<Row["tag"], string> = {
  mint: "tag",
  redeem: "tag",
  settle: "tag",
  kyc: "tag",
  fraud: "tag tag--warn",
  invariant: "tag tag--coral",
};

export function AuditPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>(SEED);

  // Demo cadence — prepend a new synthetic row every 3.5s. Replace with a
  // Tauri event subscription when the rail emits real audit lines.
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = new Date();
      const ts = now.toLocaleTimeString("en-GB", { hour12: false });
      const pick: Row = pickSynthetic(ts);
      setRows((prev) => [pick, ...prev].slice(0, 200));
    }, 3500);
    return () => window.clearInterval(id);
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__meta">D8 · PLAYBACK</div>
          <h1>
            Audit &amp; Playback
            <SeedPill label="SYNTHETIC STREAM" />
          </h1>
        </div>
        <div className="page-head__actions">
          <button className="btn btn--ghost" type="button" onClick={() => navigate("/ledger")}>
            Open ledger
          </button>
        </div>
      </div>

      <SeedBanner
        what="Synthetic event stream · new lines prepend every 3.5s with plausible mint / redeem / settle / fraud / invariant events."
        howToReal={
          <>
            To go live: expose a Tauri event channel <code>audit:line</code> on the rail
            backend, subscribe in the frontend via{" "}
            <code>listen('audit:line', …)</code>, and drop the synthetic interval below.
          </>
        }
      />

      <div className="audit-stream">
        {rows.map((r, i) => (
          <div key={`${r.ts}-${i}`}>
            <span className="ts">{r.ts}</span>
            <span className={TAG_CLASS[r.tag]}>{r.tag.toUpperCase()}</span>
            {r.body}
          </div>
        ))}
      </div>
    </>
  );
}

function pickSynthetic(ts: string): Row {
  const choices: Row[] = [
    { ts, tag: "mint", body: `BVNDLE → D1 · ${rng(200, 2400)} coins · backed · ref 0x${hex()}` },
    { ts, tag: "redeem", body: `tag ${hex()} → user_partner_transfer · MERCH#${rng(10, 99)}` },
    { ts, tag: "settle", body: `D5 → sponsor · ${pickRail()} · ₦${rng(8000, 65000)}` },
    { ts, tag: "invariant", body: `block #${rng(18430, 18450)} sealed · minted=backed · 2ms` },
    { ts, tag: "kyc", body: `D4 step-up requested for tag ${hex()} · tier T1 → T2` },
  ];
  const idx = Math.floor(Math.random() * choices.length);
  return choices[idx] ?? choices[0]!;
}
function rng(a: number, b: number): number {
  return a + Math.floor(Math.random() * (b - a));
}
function hex(): string {
  return Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0");
}
function pickRail(): string {
  return Math.random() > 0.5 ? "NIP" : "PayShap";
}
